import "@/utils/zodConfig.ts";
import { Auth0Provider } from "@auth0/auth0-react";
import AuthGuard from "@/components/AuthGuard/index.ts";
import MultiProvider from "@/components/MultiProvider/index.ts";
import "@fontsource/roboto/300.css";
import "@fontsource/roboto/400.css";
import "@fontsource/roboto/500.css";
import "@fontsource/roboto/700.css";
import { CssBaseline } from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { ReactQueryDevtools } from "@tanstack/react-query-devtools";
import { SnackbarProvider } from "notistack";
import { StrictMode } from "react";
import { ConfirmProvider } from "react-confirm-hook";
import { createRoot } from "react-dom/client";
import { RouterProvider } from "react-router-dom";
import GlobalPasteHandler from "@/components/GlobalPasteHandler";
import ThemeProvider from "@/components/ThemeProvider";
import { router } from "./router.js";

const container = document.getElementById("root");

if (!container) {
    throw new Error("Root element missing");
}

const queryClient = new QueryClient({
    defaultOptions: {
        queries: {
            refetchOnWindowFocus: false,
        },
    },
});
const root = createRoot(container);

root.render(
    <StrictMode>
        <MultiProvider
            providerCreators={[
                (children) => (
                    <ThemeProvider>
                        <CssBaseline />
                        {children}
                    </ThemeProvider>
                ),
                (children) => (
                    <QueryClientProvider client={queryClient}>
                        {children}
                        <ReactQueryDevtools initialIsOpen={false} />
                    </QueryClientProvider>
                ),
                (children) => <SnackbarProvider>{children}</SnackbarProvider>,
                (children) => <ConfirmProvider>{children}</ConfirmProvider>,
                (children) => (
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                        {children}
                    </LocalizationProvider>
                ),
                (children) => <GlobalPasteHandler>{children}</GlobalPasteHandler>,
                (children) => {
                    // if route is /client-case-status display without Auth0Provider or AuthGuard
                    if (window.location.pathname === "/client-case-status") {
                        return children;
                    }

                    return (
                        <Auth0Provider
                            domain={import.meta.env.VITE_APP_AUTH0_DOMAIN}
                            clientId={import.meta.env.VITE_APP_AUTH0_CLIENT_ID}
                            authorizationParams={{
                                redirect_uri: window.location.origin,
                                audience: import.meta.env.VITE_APP_AUTH0_AUDIENCE,
                                scope: "",
                            }}
                            onRedirectCallback={(appState) => {
                                void router.navigate(appState?.returnTo ?? "/");
                            }}
                            useRefreshTokens
                            cacheLocation="localstorage"
                        >
                            <AuthGuard>{children}</AuthGuard>
                        </Auth0Provider>
                    );
                },
            ]}
        >
            <RouterProvider router={router} />
        </MultiProvider>
    </StrictMode>,
);
